/* eslint-disable sonarjs/no-duplicate-string */
import { Route } from '@angular/router';
import { authenticationGuard } from '@warema/shared/authentication/api';
import { ConsentComponent, consentGuard } from '@warema/shared/consent';
import { AppModuleTenantName } from '@warema/shared/models';
import { MetaRouteKeysWaremaPlus } from '@warema/warema-plus/models';
import { ErrorScreenComponent } from '@warema/warema-plus/shared/components';
import { tenantGuard } from '@warema/warema-plus/tenant';
import { RootComponent } from './components/root/root.component';

export const appRoutes: Array<Route> = [
    {
        path: '',
        children: [
            {
                path: '',
                component: RootComponent,
                data: {
                    metaKey: MetaRouteKeysWaremaPlus.Default,
                },
            },
            {
                path: 'devices',
                loadChildren: () =>
                    import('@warema/warema-plus/devices/feature').then(m => m.WaremaPlusDevicesFeatureModule),
                canActivate: [authenticationGuard, tenantGuard],
                data: { moduleName: AppModuleTenantName.Devices, metaKey: MetaRouteKeysWaremaPlus.Devices },
            },
            {
                path: 'consultations',
                canActivate: [authenticationGuard, tenantGuard],
                data: { moduleName: AppModuleTenantName.Consultations, metaKey: MetaRouteKeysWaremaPlus.Consultations },
                children: [
                    {
                        path: '',
                        loadComponent: () =>
                            import('@warema/warema-plus/consultations/feature').then(m => m.ConsultationsComponent),
                    },
                    {
                        path: ':id',
                        loadComponent: () =>
                            import('@warema/warema-plus/consultations/feature').then(
                                c => c.ConsultationDetailsComponent,
                            ),
                    },
                ],
            },
            {
                path: 'appointments',
                loadChildren: () =>
                    import('@warema/warema-plus/appointments/feature').then(m => m.WaremaPlusAppointmentsFeatureModule),
                canActivate: [authenticationGuard, tenantGuard],
                data: { moduleName: AppModuleTenantName.Appointment, metaKey: MetaRouteKeysWaremaPlus.Appointments },
            },
            {
                path: 'profile',
                loadChildren: () => import('@warema/warema-plus/profile').then(m => m.WaremaPlusProfileModule),
                canActivate: [authenticationGuard, tenantGuard],
                data: { moduleName: AppModuleTenantName.Account, metaKey: MetaRouteKeysWaremaPlus.Account },
            },
            {
                path: 'products',
                loadChildren: () =>
                    import('@warema/warema-plus/products/feature').then(m => m.WaremaPlusProductsFeatureModule),
                canActivate: [authenticationGuard, tenantGuard],
                data: {
                    moduleName: AppModuleTenantName.ProductRegistration,
                    metaKey: MetaRouteKeysWaremaPlus.ProductRegistration,
                },
            },
            {
                path: 'products-registration',
                loadChildren: () =>
                    import('@warema/warema-plus/products-registration/feature').then(
                        m => m.WaremaPlusProductsRegistrationFeatureModule,
                    ),
                canActivate: [authenticationGuard, tenantGuard],
                data: {
                    moduleName: AppModuleTenantName.ProductRegistration,
                    metaKey: MetaRouteKeysWaremaPlus.ProductRegistration,
                },
            },
            {
                path: 'extended-warranty/:id',
                loadChildren: () =>
                    import('@warema/warema-plus/extended-warranty/feature').then(
                        m => m.WaremaPlusExtendedWarrantyFeatureModule,
                    ),
                canActivate: [authenticationGuard, tenantGuard],
                data: { moduleName: AppModuleTenantName.ProductRegistration },
            },
            {
                path: 'projects',
                loadChildren: () =>
                    import('@warema/warema-plus/projects/feature').then(m => m.WaremaPlusProjectsFeatureModule),
                canActivate: [authenticationGuard, tenantGuard],
                data: { moduleName: AppModuleTenantName.Projects, metaKey: MetaRouteKeysWaremaPlus.Projects },
            },
            {
                path: 'shop',
                loadChildren: () => import('@warema/shared/shop/main/feature').then(m => m.SharedShopMainFeatureModule),
                canActivate: [tenantGuard],
                data: { moduleName: AppModuleTenantName.Shop, metaKey: MetaRouteKeysWaremaPlus.Shop },
            },
            {
                path: 'order-history',
                loadChildren: () =>
                    import('@warema/shared/order-history/main/feature').then(
                        m => m.SharedOrderHistoryMainFeatureModule,
                    ),
                canActivate: [authenticationGuard, tenantGuard],
                data: { moduleName: AppModuleTenantName.Shop, metaKey: MetaRouteKeysWaremaPlus.OrderHistory },
            },
            {
                path: 'instruction-product-registrations-qr/:id',
                loadChildren: () =>
                    import('@warema/warema-plus/products-registration-qrcode/feature').then(
                        m => m.WaremaPlusProductsRegistrationQrcodeFeatureModule,
                    ),
                canActivate: [tenantGuard],
                data: { moduleName: AppModuleTenantName.ProductRegistration },
            },
            {
                path: 'error',
                component: ErrorScreenComponent,
            },
            {
                path: 'consent',
                component: ConsentComponent,
            },
            {
                path: 'appointment-workflow',
                loadChildren: () =>
                    import('@warema/shared/workflows/appointment-workflow/feature').then(
                        m => m.WaremaPlusAppointmentWorkflowFeatureModule,
                    ),
                canActivate: [authenticationGuard, tenantGuard, consentGuard],
                data: { moduleName: AppModuleTenantName.Appointment, consent: 'Google Maps' },
            },
            {
                path: 'offer-workflow',
                loadChildren: () =>
                    import('@warema/shared/workflows/offer-workflow/feature').then(
                        m => m.WaremaPlusOfferWorkflowFeatureModule,
                    ),

                canActivate: [consentGuard],
                data: { moduleName: AppModuleTenantName.OfferWorkflow, consent: 'Google Maps' },
            },
            {
                path: 'company-search-workflow',
                loadChildren: () =>
                    import('@warema/shared/workflows/company-search-workflow/feature').then(
                        m => m.CompanySearchWorkflowFeatureModule,
                    ),
                data: { consent: 'Google Maps', moduleName: AppModuleTenantName.CompanySearch },
                canActivate: [consentGuard, tenantGuard],
            },
            {
                path: 'company-search',
                redirectTo: 'company-search-workflow',
            },
            {
                path: 'serviceappointment/:token',
                loadChildren: () =>
                    import('@warema/warema-plus/automated-service-appointment/feature').then(
                        m => m.WaremaPlusAutomatedServiceAppointmentFeatureModule,
                    ),
                data: { moduleName: AppModuleTenantName.AutomatedServiceAppointment },
            },
            {
                path: 'consultation-link-expired',
                loadComponent: () =>
                    import('@warema/warema-plus/consultations/feature').then(m => m.ExpiredRegistrationLinkComponent),
            },
            {
                path: 'consultation/SignUp',
                loadComponent: () =>
                    import('@warema/warema-plus/consultations/feature').then(m => m.ConsultationSignUpComponent),
            },

            { path: '**', redirectTo: '' },
        ],
    },
];
